// Services
import ConfigService from '@/services/ConfigService';
import InsuranceTypeService from '@/services/InsuranceTypeService';
import PaymentService from '@/services/PaymentService';
import PolicyService from '@/services/PolicyService';
import TokenService from '@/services/TokenService';

// Utitlities
import _ from 'lodash';

// Constants
import constants from '@/i18n/en/constants';
const VUEX_DATA = 'vuex';
const ERROR_CASE = {
  GENERAL: 'generalError',
  UNKNOWN_FAILURE: 'unknownFailure',
  KNOWN_PAYMENT: 'knownPaymentError'
}

const TC_ERRORS = {
  CVV: 'Error calling TC binding: The submitted CVV is incorrect.',
  CALL: 'Error calling TC binding: Authorization is required for this payment. ' +
    'Please call the service number for your bank or credit card provider.',
  CARD_ERROR: 'Error calling TC binding: Invalid card number—please verify the information you’ve entered ' +
    'or try again with a different payment method. If the issue persists, ' +
    'please call your bank or credit card provider for assistance.',
  // decline, fraud, blacklist, and velocity decline types generate the same error from the TC binding/payment mediation
  DECLINE: 'Error calling TC binding: Your credit card or e-check was declined. ' +
    'Please verify the information you’ve entered or try again with a different payment method. ' +
    'If the issue persists, please call your bank or credit card provider for assistance.',
  DUPLICATE: 'Error calling TC binding: This appears to be a duplicate payment. Please allow 24 hours ' +
    'from your last payment to attempt another.'
};

/* --- ACTIONS --- */

/**
 * Actions receive the store as their first argument then other inputs.
 *
 * @param { commit, state, dispatch, getter } store - the store object
 * @param {*} payload - the data with which the state should be updated. All data must be in one payload
 */

const PAYMENT_STATES = {
  USER_SUBMIT_STARTED: 'UserSubmitStarted',
  USER_SUBMITTED: 'UserSubmitted'
};

const FIND_YOUR_BILL_FORM_SUBMITTED = ({ commit, state }) => {
  return TokenService.getPolicyZipToken(state.policyNumber, state.zipCode)
    .then(() => {
      return Promise.all([
        PolicyService.getPolicyBalanceInfo(state.policyNumber, state.zipCode),
        PaymentService.createPaymentResource(state.policyNumber, state.zipCode, state.returnUrl, state.testMode)
      ]);
    })
    .then(([policyBalanceInfo, paymentResource]) => {
      commit('FETCH_POLICY_BALANCE_INFO_SUCCESS', policyBalanceInfo.data);
      commit('CREATE_PAYMENT_RESOURCE_SUCCESS', paymentResource.data);
    })
    .catch(err => {
      let errorMessage = err.response.data.message;
      if (errorMessage === 'Invalid-Policy-Zip') {
        commit('ADD_ERROR', errorMessage);
      } else {
        // Any error that is not from the policy/zip combo throwing an error will route to the general error
        commit('ADD_ERROR', ERROR_CASE.GENERAL);
      }

      throw err;
    });
};

const CREATE_PAYMENT_RESOURCE = ({ commit, state }) => {
  return PaymentService.createPaymentResource(state.policyNumber, state.zipCode, state.returnUrl, state.testMode)
    .then(res => {
      commit('CREATE_PAYMENT_RESOURCE_SUCCESS', res.data);
    })
    .catch(err => {
      commit('ADD_ERROR', ERROR_CASE.GENERAL);
      throw err;
    });
};

const UPDATE_PAYMENT_RESOURCE_SUBMIT_STARTED = ({ commit, state }) => {
  return PaymentService.updatePaymentResource(state.policyNumber,
    state.zipCode,
    state.paymentResource.id,
    PAYMENT_STATES.USER_SUBMIT_STARTED,
    state.payment.cardInfo.number,
    state.payment.bankInfo.accountType)
    .then(res => {
      if (res.data.state === 'UserSubmitStarted') {
        commit('UPDATE_PAYMENT_RESOURCE_SUCCESS', res.data);
        return res;
      } else {
        throw new Error('Payment state is not currently set to UserSubmitStarted');
      }
    })
    .catch(err => {
      if (_.has(err, 'response.data')) {
        let errData = err.response.data;

        if (errData.status === 422 || errData.status === 502) {
          // 422 error status, handles the error listed below
          // missingfields, badformat, extrafields, badlength, merchantcantaccept, state fails
          commit('ADD_ERROR', ERROR_CASE.KNOWN_PAYMENT);
        } else if (errData.status === 401 || errData.status === 403) {
          // Unauthorized status 401 / Forbidden status 403.
          commit('ADD_ERROR', ERROR_CASE.GENERAL);
          commit('REMOVE_TOKEN_SESSION_STORAGE');
        } else if (errData.status === 500 || errData.status === 503) {
          commit('ADD_ERROR', ERROR_CASE.UNKNOWN_FAILURE);
        } else {
          commit('ADD_ERROR', ERROR_CASE.UNKNOWN_FAILURE);
        }
      } else {
        // Handle the case of a lost internet connection (Error: Network Error)
        // Should also handle other errors that don't contain response data
        commit('ADD_ERROR', ERROR_CASE.UNKNOWN_FAILURE);
      }

      throw err;
    });
};

const UPDATE_PAYMENT_RESOURCE_SUBMITTED = async ({ commit, state }) => {
  let response = await PaymentService.updatePaymentResource(state.policyNumber,
    state.zipCode,
    state.paymentResource.id,
    PAYMENT_STATES.USER_SUBMITTED,
    state.payment.cardInfo.number,
    state.payment.bankInfo.accountType)
    .then(res => {
      commit('UPDATE_PAYMENT_RESOURCE_SUCCESS', res.data);
      commit('REMOVE_USER_PAYMENT_DATA');
    })
    .catch(err => {
      if (_.has(err, 'response.data')) {
        const errData = err.response.data;

        if (errData.status === 422) {
          // 422 error status, handles the error listed below
          // missingfields, badformat, extrafields, badlength, merchantcantaccept, state fails
          commit('ADD_ERROR', ERROR_CASE.KNOWN_PAYMENT);
        } else if (errData.status === 502) {
          // Payment failures due to error returned from TC
          switch (errData.message) {
            case TC_ERRORS.CVV:
              commit('ADD_ERROR', 'tcErrorCvv');
              break;
            case TC_ERRORS.CALL:
              commit('ADD_ERROR', 'tcErrorCall');
              break;
            case TC_ERRORS.CARD_ERROR:
              commit('ADD_ERROR', 'tcErrorCarderror');
              break;
            case TC_ERRORS.DUPLICATE:
              commit('ADD_ERROR', 'tcErrorDuplicate');
              break;
            case TC_ERRORS.DECLINE:
            default:
              commit('ADD_ERROR', 'tcErrorDecline');
              break;
          }
        } else if (errData.status === 401 || errData.status === 403) {
          // Unauthorized: status error 401 - 403.
          commit('ADD_ERROR', ERROR_CASE.GENERAL);
          commit('REMOVE_TOKEN_SESSION_STORAGE');
        } else if (errData.status === 500 || errData.status === 503) {
          commit('ADD_ERROR', ERROR_CASE.UNKNOWN_FAILURE);
        } else {
          commit('ADD_ERROR', ERROR_CASE.UNKNOWN_FAILURE);
        }
      } else {
        // Handle the case of a lost internet connection (Error: Network Error)
        // Should also handle other errors that don't contain response data
        commit('ADD_ERROR', ERROR_CASE.UNKNOWN_FAILURE);
      }

      throw err;
    });

  return response;
};

/**
 * Update vuex store and remove vuex data from sessionStorage.
 */
const UPDATE_STATE = ({ commit }) => {
  return new Promise((resolve, reject) => {
    if (sessionStorage.getItem(VUEX_DATA)) {
      commit('POPULATE_STATE', JSON.parse(sessionStorage.getItem(VUEX_DATA)));
      sessionStorage.removeItem(VUEX_DATA);
      resolve();
    }
    reject(new Error());
  });
};

const SET_VUEX_STATE_IN_SESSION_STORAGE = ({ state }) => {
  sessionStorage.setItem(VUEX_DATA, JSON.stringify(state));
};

/**
 * Cancel the transaction when user clicks on Cancel button
 * This function receive the current insuranceType to be cloned
 * Commit RESET_USER_TRANSACTION_DATA to update insuranceType and reset user transaction data
 */
const CANCEL_TRANSACTION = ({ commit }) => {
  commit('RESET_USER_TRANSACTION_DATA');
};

/**
 * Uses ConfigService to check whether maintenance mode is enabled.
 */
const FETCH_MAINTENANCE_MODE_STATUS = ({ commit }) => {
  return ConfigService.isMaintenanceModeEnabled()
    .then(res => {
      return res;
    })
    .catch(err => {
      // If an error occurs when fetching the maintance mode status from the config, handle as a general error
      commit('ADD_ERROR', ERROR_CASE.GENERAL);
      throw err;
    })
}

/**
 * Uses InsuranceTypeService to retrieve info about the insurance type involved with this payment.
 *
 * @param {*} insuranceTypeQuery - the insurance type name, provided as a query parameter
 */
const FETCH_INSURANCE_TYPE = ({ commit }, insuranceTypeQuery) => {
  let sfmDefault = constants.sfmInsuranceTypeKeyDefault;
  let insuranceTypeKey = insuranceTypeQuery || sfmDefault;

  commit('INSURANCE_TYPE_REQUESTED', insuranceTypeKey);

  InsuranceTypeService.getInsuranceType(insuranceTypeKey)
    .catch(err => {
      if (_.get(err, 'response.status') === 404) {
        return InsuranceTypeService.getInsuranceType(sfmDefault);
      } else {
        throw err;
      }
    })
    .then(res => {
      let insuranceType = {
        ...res.data
      };
      commit('FETCH_INSURANCE_TYPE_SUCCESS', insuranceType);
    })
    .catch(err => {
      throw err;
    });
};

export default {
  FIND_YOUR_BILL_FORM_SUBMITTED,
  CREATE_PAYMENT_RESOURCE,
  UPDATE_PAYMENT_RESOURCE_SUBMIT_STARTED,
  UPDATE_PAYMENT_RESOURCE_SUBMITTED,
  UPDATE_STATE,
  SET_VUEX_STATE_IN_SESSION_STORAGE,
  CANCEL_TRANSACTION,
  FETCH_MAINTENANCE_MODE_STATUS,
  FETCH_INSURANCE_TYPE
}



// WEBPACK FOOTER //
// ./src/store/actions.js